import { useKindeAuth } from '@kinde-oss/kinde-auth-react';
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'src/lib/routing';
import { isExternalURL } from 'shared-utils/validation';
import { useAddUTMToAppState } from 'src/hooks/useAddUTMToAppState';

import { useIsKinde } from 'containers/Auth/AuthWrapper';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { globalVariables } from 'lib/globalVariables';
import { localStorage } from 'lib/storage';
import { userUtils } from 'lib/user';
import { utils } from 'lib/utils';
import { clearImpersonation, getImpersonationToken, setImpersonationToken } from 'utils/impersonation';
import { decodeToken } from 'utils/token-utils';

import { resolveAuthedRedirect } from './resolveAuthedRedirect';

import type { UserRoles } from 'containers/Settings/types';

type LocationState = {
  email?: string;
  googleAccountError?: string;
  loginToJoinTeam?: boolean;
  from?: {
    pathname?: string;
  };
};

export const LoginContainer = () => {
  const clientContext = useConnectedClient();
  const { isAuthenticated, login } = useKindeAuth();
  const isKinde = useIsKinde();

  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState | null;
  const extraArguments = useRef<URLSearchParams | null>(undefined);
  // Once an authenticated visitor's redirect has been kicked off we must not do
  // it again: `navigate` re-triggers this effect before the navigation commits,
  // so without this guard the redirect fires in a tight synchronous render loop.
  const hasRedirected = useRef(false);
  const { addUTMToAppState } = useAddUTMToAppState();
  useEffect(() => {
    // Get extra arguments coming from the URL
    const args = utils.parseQueryParams(location?.search);
    extraArguments.current = args;
    if (isAuthenticated) {
      if (hasRedirected.current) {
        return;
      }
      hasRedirected.current = true;

      return navigate(resolveAuthedRedirect(args?.get('redirectPath')));
    }
    // For admin, we get token from the URL, and for the client, we get it from sessionStorage
    const token = args?.get('token') || getImpersonationToken();
    const userType = args?.get('user_type') || 'client';
    const { redirectPath } = (() => {
      const paramRedirectPath = args?.get('redirectPath');
      if (paramRedirectPath) {
        return { redirectPath: paramRedirectPath };
      }
      return { redirectPath: null };
    })();

    void (async () => {
      if (!token && isKinde) {
        const orgCode = args?.get('org_code');
        await login({
          ...(orgCode ? { orgCode } : {}),
          state: addUTMToAppState({
            user_type: userType,
            ...(redirectPath ? { redirect_uri: redirectPath } : {}),
          }),
        });

        return;
      }

      const decodedToken = decodeToken(token);

      const userId = decodedToken?.user_id ?? args?.get('user_id');

      // The token carries roles as a list; the query param carries the same list comma-separated.
      const roles = decodedToken?.roles ?? (args?.get('roles')?.split(',') as UserRoles[] | undefined);

      // If we have everything necessary to log an user in, it should log him in
      if (token && userId) {
        // Before processing everything, it should first test whether a connection to a demo_client is trying to get made
        // If that's the case, then it should check whether the client isn't connected already.
        //  Connected -> Don't disconnect the user
        //  Disconnected -> Connect to the demo client account
        if (!(userUtils.isDemoClient(roles) && clientContext.authState === 'authenticated')) {
          // Resets all storage to make sure its clean
          clearImpersonation();
          localStorage.reset();

          clientContext.actions.setAccessToken(token);

          // Store impersonation token and flag in sessionStorage
          setImpersonationToken(token);
          if (globalVariables.getEnvironmentVariables().environment === 'DEV') {
            localStorage.save('ignoreWarning', true);
          }
        }

        if (extraArguments.current?.get('ignoreWarning') || extraArguments.current?.get('ignoreWarning') === 'true') {
          localStorage.save('ignoreWarning', true);
        }

        const isExternal = isExternalURL(redirectPath);

        if (redirectPath && !isExternal) {
          // Redirects the user to the specific route
          navigate({ pathname: redirectPath });
        } else {
          // Redirects the user to the dashboard
          navigate({ pathname: '/' });
        }
        return;
      }

      if (!extraArguments.current && clientContext.authState === 'authenticated') {
        // Redirects the user to the dashboard
        navigate({ pathname: '/' });
        localStorage.save(
          'teamIgnoreTracking',
          clientContext?.details?.ConnectedTeam?.settings?.ignore_tracking || false,
        );
        return;
      }

      navigate({ pathname: '/logout' }, { replace: true });
      console.error('Login flow is not being handled correctly.');
    })();
  }, [
    isAuthenticated,
    clientContext.details,
    locationState?.email,
    locationState?.googleAccountError,
    location?.search,
    location.pathname,
    clientContext.authState,
    clientContext.actions,
    navigate,
  ]);

  return null;
};
